import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
    authConfig,
    getAccessToken as getCognitoAccessToken,
    getStoredTokens,
    handleAuthRedirect,
    hasValidAccessToken,
    signIn as signInWithCognito,
    signOut as signOutFromCognito,
} from "./cognitoAuth";

const AuthContext = createContext(null);
const localAuthEnabled =
    process.env.REACT_APP_AUTH_MODE === "local" ||
    (process.env.NODE_ENV === "development" && (!authConfig.domain || !authConfig.clientId));

const localSession = {
    access_token: "local-development-token",
    accessTokenExpiresAt: Date.now() + 24 * 60 * 60 * 1000,
    username: "local@sandbox",
};

export const AuthProvider = ({ children }) => {
    const [loading, setLoading] = useState(true);
    const [session, setSession] = useState(null);
    const [error, setError] = useState("");

    const loadSession = useCallback(async () => {
        setLoading(true);
        setError("");

        try {
            if (localAuthEnabled) {
                setSession(localSession);
                return;
            }

            const redirectedSession = await handleAuthRedirect();
            if (redirectedSession) {
                setSession(redirectedSession);
                return;
            }

            const storedSession = getStoredTokens();
            if (!storedSession) {
                setSession(null);
                return;
            }

            if (hasValidAccessToken(storedSession)) {
                setSession(storedSession);
                return;
            }

            signOutFromCognito();
        } catch (authError) {
            setSession(null);
            setError(authError.message || "No se pudo validar la sesion.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadSession();
    }, [loadSession]);

    const signIn = useCallback(async () => {
        if (localAuthEnabled) {
            setSession(localSession);
            return;
        }

        return signInWithCognito();
    }, []);

    const signOut = useCallback(() => {
        if (localAuthEnabled) {
            setSession(localSession);
            return;
        }

        signOutFromCognito();
    }, []);

    const getAccessToken = useCallback(async () => {
        if (localAuthEnabled) return localSession.access_token;
        return getCognitoAccessToken();
    }, []);

    const value = useMemo(
        () => ({
            configured: localAuthEnabled || Boolean(authConfig.domain && authConfig.clientId),
            error,
            getAccessToken,
            loading,
            session,
            signIn,
            signOut,
            reload: loadSession,
        }),
        [error, getAccessToken, loading, session, loadSession, signIn, signOut]
    );

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error("useAuth debe usarse dentro de AuthProvider.");
    }
    return context;
};
